// ============================================================================
// WeekView — the My Route "This Week" / "Next Week" reading view. Groups a
// week of stops Mon–Sun from v_am_routes_week, filtered to the target week's
// (parity, week_of_month) by route-week-math. Read-only: editing days /
// parity stays on the Today view + template editor (handoff §3A).
//
// Mobile-first: each day is a collapsible section; today's section is
// expanded + highlighted (This Week only). Each stop shows the facility with
// its owner / main contact in parens, RAG dot, cadence/parity, as-able flag,
// and note — so AMs recognize a stop by who runs it.
// ============================================================================

import { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { RagDot } from '../../../shared/components';
import { ServiceStateChip } from '../../account-management/stop-service-parts';
import {
  CADENCE_LABELS,
  WEEKDAYS,
  type DayOfWeek,
  type WeekViewRow,
} from '../hooks/use-am-routes-data';
import {
  localISO,
  mondayOf,
  stopMatchesWeek,
  type WeekTarget,
} from '../lib/route-week-math';

// Short labels for the day headers.
const DAY_SHORT: Record<DayOfWeek, string> = {
  Monday: 'Mon',
  Tuesday: 'Tue',
  Wednesday: 'Wed',
  Thursday: 'Thu',
  Friday: 'Fri',
  Saturday: 'Sat',
  Sunday: 'Sun',
};

// The local weekday name for `date`, matching DayOfWeek labels.
function weekdayName(date: Date): DayOfWeek {
  return WEEKDAYS[(date.getDay() + 6) % 7];
}

// The calendar date of `day` within the target week (for the header chip).
function dateForDay(target: WeekTarget, day: DayOfWeek): Date {
  const monday = mondayOf(new Date(`${target.weekStart}T00:00:00`));
  const d = new Date(monday);
  d.setDate(monday.getDate() + WEEKDAYS.indexOf(day));
  return d;
}

export interface WeekViewProps {
  rows: WeekViewRow[];
  target: WeekTarget;
  /** Highlight + auto-expand today's column (This Week only). */
  highlightToday?: boolean;
  /** Open a facility's account page (AFB-0063 R-1). Rows without a
   *  facility_id (office stops etc.) are not tap targets. */
  onOpenFacility?: (facilityId: string) => void;
}

export default function WeekView({
  rows,
  target,
  highlightToday,
  onOpenFacility,
}: WeekViewProps) {
  const todayName = highlightToday ? weekdayName(new Date()) : null;
  const todayISO = localISO(new Date());

  // Stops for this target week, bucketed by day in route order.
  const byDay = useMemo(() => {
    const map = new Map<DayOfWeek, WeekViewRow[]>();
    for (const day of WEEKDAYS) map.set(day, []);
    for (const r of rows) {
      if (!r.day_of_week) continue;
      if (!stopMatchesWeek(r, target)) continue;
      map.get(r.day_of_week as DayOfWeek)?.push(r);
    }
    for (const day of WEEKDAYS) {
      map
        .get(day)!
        .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
    }
    return map;
  }, [rows, target]);

  const totalStops = useMemo(
    () => WEEKDAYS.reduce((n, d) => n + (byDay.get(d)?.length ?? 0), 0),
    [byDay],
  );

  // Days the AM closes (Sat/Sun) usually have nothing — only render a day
  // section when it has stops, or it's today (so "nothing today" is explicit).
  const visibleDays = WEEKDAYS.filter(
    (d) => (byDay.get(d)?.length ?? 0) > 0 || d === todayName,
  );

  if (totalStops === 0) {
    return (
      <div className="mt-4 rounded-xl border border-tcs-border bg-tcs-bg p-6 text-center text-sm text-tcs-text-secondary">
        No scheduled stops for this week.
      </div>
    );
  }

  return (
    <div className="mt-4 space-y-2">
      {visibleDays.map((day) => {
        const stops = byDay.get(day) ?? [];
        const dayDate = dateForDay(target, day);
        const isToday =
          highlightToday && localISO(dayDate) === todayISO && day === todayName;
        return (
          <DaySection
            key={day}
            day={day}
            dateLabel={`${dayDate.getMonth() + 1}/${dayDate.getDate()}`}
            stops={stops}
            isToday={!!isToday}
            defaultOpen={!!isToday || stops.length > 0}
            onOpenFacility={onOpenFacility}
          />
        );
      })}
    </div>
  );
}

function DaySection({
  day,
  dateLabel,
  stops,
  isToday,
  defaultOpen,
  onOpenFacility,
}: {
  day: DayOfWeek;
  dateLabel: string;
  stops: WeekViewRow[];
  isToday: boolean;
  defaultOpen: boolean;
  onOpenFacility?: (facilityId: string) => void;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const empty = stops.length === 0;

  return (
    <section
      className={`overflow-hidden rounded-xl border ${
        isToday
          ? 'border-tcs-accent/60 bg-tcs-accent/5'
          : 'border-tcs-border bg-tcs-raised'
      }`}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between px-3 py-2.5 text-left"
      >
        <span className="flex items-center gap-2">
          {open ? (
            <ChevronDown className="h-4 w-4 text-tcs-text-muted" />
          ) : (
            <ChevronRight className="h-4 w-4 text-tcs-text-muted" />
          )}
          <span className="text-sm font-semibold text-tcs-text">
            {DAY_SHORT[day]}
          </span>
          <span className="text-xs text-tcs-text-muted">{dateLabel}</span>
          {isToday && (
            <span className="rounded-full bg-tcs-accent-ink px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-tcs-accent-on">
              Today
            </span>
          )}
        </span>
        <span className="text-xs font-medium text-tcs-text-secondary">
          {empty ? 'No stops' : `${stops.length} stop${stops.length === 1 ? '' : 's'}`}
        </span>
      </button>

      {open && !empty && (
        <ul className="divide-y divide-tcs-border/70 border-t border-tcs-border/70">
          {stops.map((r) => (
            <WeekStopRow
              key={r.assignment_id}
              row={r}
              onOpen={
                onOpenFacility && r.facility_id
                  ? () => onOpenFacility(r.facility_id!)
                  : undefined
              }
            />
          ))}
        </ul>
      )}
    </section>
  );
}

// facility_name (owner/POC) — display_contact (worker_name|primary_poc_name)
// first, falling back to poc_initials, then nothing.
function contactSuffix(r: WeekViewRow): string | null {
  const name = r.display_contact?.trim() || r.poc_initials?.trim() || null;
  return name;
}

function WeekStopRow({
  row,
  onOpen,
}: {
  row: WeekViewRow;
  onOpen?: () => void;
}) {
  const facility = row.facility_name ?? row.stop_label ?? 'Untitled stop';
  const contact = contactSuffix(row);
  const parityTag =
    row.cadence === 'Biweekly' &&
    row.week_parity &&
    row.week_parity !== 'A_B'
      ? ` · ${row.week_parity}`
      : '';

  return (
    <li
      onClick={onOpen}
      {...(onOpen
        ? {
            role: 'link' as const,
            tabIndex: 0,
            'aria-label': `Open ${facility}`,
            onKeyDown: (e: React.KeyboardEvent) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onOpen();
              }
            },
          }
        : {})}
      className={`px-3 py-2.5 ${
        onOpen ? 'cursor-pointer transition-colors hover:bg-tcs-bg-muted/50 active:bg-tcs-bg-muted' : ''
      }`}
    >
      <div className="flex items-start gap-2">
        <span className="pt-1">
          <RagDot status={row.account_status_rag} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-tcs-text">
            <span className="break-words">{facility}</span>
            {contact && (
              <span className="font-normal text-tcs-text-secondary">
                {' '}
                ({contact})
              </span>
            )}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-1.5">
            {row.cadence && (
              <span className="rounded-full bg-tcs-surface px-2 py-0.5 text-[11px] font-medium text-tcs-text-secondary">
                {CADENCE_LABELS[row.cadence]}
                {parityTag}
              </span>
            )}
            <ServiceStateChip state={row.service_state} size="sm" />
            {row.is_as_able && (
              <span className="rounded-full bg-st-warn-bg-strong px-2 py-0.5 text-[11px] font-medium text-st-warn-fg">
                As able
              </span>
            )}
            {row.coverage === 'assist' && (
              <span className="rounded-full bg-violet-100 px-2 py-0.5 text-[11px] font-semibold text-violet-700">
                Helping {(row.owner_name ?? '').split(' ')[0] || 'AM'}
              </span>
            )}
          </div>
          {row.note && (
            <p className="mt-1 text-xs text-tcs-text-secondary">{row.note}</p>
          )}
        </div>
      </div>
    </li>
  );
}
